<script lang="ts">
	import type { WithElementRef } from 'bits-ui'
	import type { HTMLAttributes } from 'svelte/elements'
	import { cn } from 'tailwind-variants'

	import ChartStyle from './chart-style.svelte'
	import { setChartContext, type ChartConfig } from './chart-utils.js'

	const uid = $props.id()

	let {
		ref = $bindable(null),
		id = uid,
		class: className,
		children,
		config,
		...restProps
	}: WithElementRef<HTMLAttributes<HTMLElement>> & {
		config: ChartConfig
	} = $props()

	const chartId = $derived(`chart-${id || uid.replace(/:/g, '')}`)

	setChartContext({
		get config() {
			return config
		},
	})
</script>

<div
	bind:this={ref}
	class={cn(
		'flex aspect-video justify-center overflow-visible text-xs',
		// Overrides
		//
		// Stroke around dots/marks when hovering
		'[&_.lc-highlight-point]:stroke-transparent',
		// override the default stroke color of lines
		'[&_.lc-line]:stroke-border/50',

		// Show the crosshair line when hovering, like the previous unovis charts
		'[&_.lc-line.lc-highlight-line]:stroke-muted-foreground [&_.lc-highlight-line]:stroke-1 [&_.lc-highlight-line]:[stroke-dasharray:none]',

		// by default, when you hover a point on a stacked series chart, it will drop the opacity
		// of the other series, this overrides that
		'[&_.lc-area-path]:opacity-100 [&_.lc-highlight-line]:opacity-100 [&_.lc-highlight-point]:opacity-100 [&_.lc-spline-path]:opacity-100 [&_.lc-text]:text-xs [&_.lc-text-svg]:overflow-visible',

		// Keep the tick lines and axis lines (enabled per chart via `rule` on `xAxis`/`yAxis`),
		// matching the previous unovis charts. Disable with `tickMarks: false` per axis.
		// (`.lc-line` qualifier outranks the `.lc-line` stroke override above)
		'[&_.lc-line.lc-axis-tick]:stroke-muted-foreground [&_.lc-line.lc-axis-rule]:stroke-muted-foreground',
		// Grid lines drawn per tick by an axis (`grid: true` on `xAxis`/`yAxis`)
		'[&_.lc-line.lc-axis-grid]:stroke-border',

		// We don't want to display the chart-level rule on the x/y axis, as there is already going
		// to be a grid line there and rule ends up overlapping the marks because it is rendered
		// after the marks
		'[&_.lc-rule-x-line:not(.lc-grid-x-rule,.lc-axis-rule,.lc-axis-grid)]:stroke-0 [&_.lc-rule-y-line:not(.lc-grid-y-rule,.lc-axis-rule,.lc-axis-grid)]:stroke-0',
		'[&_.lc-grid-x-radial-line]:stroke-border [&_.lc-grid-x-radial-circle]:stroke-border',
		'[&_.lc-grid-y-radial-line]:stroke-border [&_.lc-grid-y-radial-circle]:stroke-border',

		// Legend adjustments
		'[&_.lc-legend-swatch-button]:items-center [&_.lc-legend-swatch-button]:gap-1.5',
		'[&_.lc-legend-swatch-group]:items-center [&_.lc-legend-swatch-group]:gap-4',
		'[&_.lc-legend-swatch]:size-2.5 [&_.lc-legend-swatch]:rounded-[2px]',

		// Labels
		'[&_.lc-labels-text:not([fill])]:fill-foreground [&_text]:stroke-transparent',

		// Tick labels on th x/y axes
		'[&_.lc-axis-tick-label]:fill-muted-foreground [&_.lc-axis-tick-label]:font-normal',
		'[&_.lc-tooltip-rects-g]:fill-transparent',
		'[&_.lc-layout-svg-g]:fill-transparent',
		'[&_.lc-root-container]:w-full',
		className
	)}
	data-chart={chartId}
	data-slot="chart"
	{...restProps}
>
	<ChartStyle id={chartId} {config} />
	{@render children?.()}
</div>
